import {
  is as immutableCompare,
  List,
  Map as ImmutableMap,
  OrderedMap,
  OrderedSet,
  Record,
  Set
} from 'immutable';
import moment from 'moment';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { decorateEnumClass, Enum } from './genEnum';

export const FieldSerialization = decorateEnumClass(class FieldSerialization extends Enum {
  /** @type {FieldSerialization} */ static FROM;
  /** @type {FieldSerialization} */ static TO;
  /** @type {FieldSerialization} */ static ALWAYS;
  /** @type {FieldSerialization} */ static NEVER;
});

function parseComplexType(type, name) {
  const match = /^(\w+)<([\w<>,]+)>$/.exec(type);
  if (match === null) {
    throw new Error(`Invalid model field (${name}) type: ${type}`);
  }

  const immutableType = match[1];
  const subType = match[2];

  let value;
  let key;

  if (subType.includes('<')) {
    const subTypeMatch = /^((?:\w+)|(?:\w+<.*)),((?:\w+)|(?:\w+<.*))$/.exec(subType);
    if (subTypeMatch === null) {
      throw new Error(`Invalid model field (${name}) type: ${type}`);
    }

    value = subTypeMatch[1];
    key = subTypeMatch[2];
  } else {
    [value, key] = subType.split(',');
  }

  return { immutableType, subType, value, key };
}

function toOptionalPropType(field, name) {
  const { type } = field;

  if (typeof type === 'string') {
    switch (type) { // eslint-disable-line default-case
      case 'any':
      case 'bool':
      case 'number':
      case 'object':
      case 'string': return PropTypes[type];
      case 'function': return PropTypes.func;
      case 'enum': {
        if (field.isClassEnum) {
          return PropTypes.instanceOf(field.enum);
        }

        if (field.enum.valueArray) {
          return PropTypes.oneOf(field.enum.valueArray);
        }

        if (Array.isArray(field.enum)) {
          return PropTypes.oneOf(field.enum);
        }

        return PropTypes.oneOf([...field.enum]); // Assume iterable, for example: Immutable.Set
      }
      case 'moment': return PropTypes.instanceOf(moment);
      case 'model': {
        if (typeof field.model !== 'function') {
          throw new Error(`Invalid model field (${name}) type is model but constructor is of type: ${typeof field.model}`);
        }

        const serialization = field.serialization;

        const expectFromJSON =
          [FieldSerialization.ALWAYS, FieldSerialization.FROM].includes(serialization);
        const implementsFromJSON = typeof field.model.fromJSON === 'function';

        if (expectFromJSON && !implementsFromJSON) {
          throw new Error(`Invalid model field (${name}). Missing or invalid fromJSON declaration, expected function but found: ${typeof field.model.fromJSON}`);
        }

        const expectToJSON =
          [FieldSerialization.ALWAYS, FieldSerialization.TO].includes(serialization);
        const implementsToJSON = typeof field.model.prototype.toJSON === 'function';

        if (expectToJSON && !implementsToJSON) {
          throw new Error(`Invalid model field (${name}). Missing or invalid toJSON declaration, expected function but found: ${typeof field.model.fromJSON}`);
        }

        return PropTypes.instanceOf(field.model);
      }
    }

    const { immutableType, subType, value, key } = parseComplexType(type, name);
    const immutablePropType = `${immutableType.slice(0, 1).toLowerCase()}${immutableType.slice(1)}Of`;

    if (key) {
      return ImmutablePropTypes[immutablePropType](
        toPropType({ ...field, type: value, optional: true }, `${name}.value`), // eslint-disable-line
        toPropType({ ...field, type: key, optional: true }, `${name}.key`) // eslint-disable-line
      );
    }

    return ImmutablePropTypes[immutablePropType](
      toPropType({ ...field, type: subType, optional: true }, `${name}.value`) // eslint-disable-line
    );
  }

  if (typeof type === 'function') {
    return type;
  }

  throw new Error(`Invalid model field (${name}) of type ${typeof type} expected string or function. Actual value: ${type}`);
}

function toPropType(field, name) {
  if (field.optional) {
    return toOptionalPropType(field, name);
  }

  return toOptionalPropType(field, name).isRequired;
}

function parseJSONToType(field, name, jsonValue) {
  if (field.optional && jsonValue === null) {
    return null;
  }

  if (field.type === 'moment') {
    if (typeof jsonValue === 'number') {
      return moment.unix(jsonValue);
    }
    return moment.utc(jsonValue);
  }

  if (field.type === 'model') {
    return field.model.fromJSON(jsonValue);
  }

  if (field.isClassEnum) {
    return field.enum[jsonValue];
  }

  if (field.type.includes('<')) {
    const { immutableType, subType, value } = parseComplexType(field.type, name);

    switch (immutableType) {
      case 'List': return List(jsonValue)
        .map(it => parseJSONToType({ ...field, type: subType }, `${name}.value`, it));
      case 'Map': return ImmutableMap(jsonValue)
        .map(it => parseJSONToType({ ...field, type: value }, `${name}.value`, it));
      case 'OrderedMap': return OrderedMap(jsonValue)
        .map(it => parseJSONToType({ ...field, type: value }, `${name}.value`, it));
      case 'OrderedSet': return OrderedSet(jsonValue)
        .map(it => parseJSONToType({ ...field, type: subType }, `${name}.value`, it));
      case 'Set': return Set(jsonValue)
        .map(it => parseJSONToType({ ...field, type: subType }, `${name}.value`, it));
      default: throw new Error(`Unsupported immutable type ${immutableType} on field ${name}`);
    }
  }

  return jsonValue;
}

function noopValidator() {}
noopValidator.isRequired = noopValidator();

function genPropTypeDeclaration(fields) {
  if (process.env.NODE_ENV !== 'production') {
    return ImmutablePropTypes.recordOf(fields.map(toPropType).toObject());
  }

  return noopValidator;
}

/**
 * Generates a new Model class based on Immutable Record.
 * ============================
 *
 * General usage:
 * --------------
 *
 * class MyModel extends genModel({
 *   model_id: { type: 'string', default: '' }
 * }) {}
 *
 * class MyOtherModel extends genModel({
 *   my_models: { type: 'OrderedMap<model,string>', model: MyModel, default: OrderedMap() }
 * }) {
 *   static fromJSON(json) {
 *     return new MyOtherModel(MyOtherModel.parseJSON({
 *       ...json,
 *       my_models: (json.my_models || []).map(it => [it.model_id, it])
 *     }));
 *   }
 * }
 *
 * genModel could basically create the whole class on it's own, but doing so would
 * result in the all of the generated models to have the JS name attribute of Model.
 * This name attribute cannot be overridden, thus the need extend the created class
 * locally.
 *
 * static fromJSON needs to be overridden only if the json data needs to be manipulated,
 * for example above, mapping the array of models to map with model id as the key.
 *
 *
 * Field declarations:
 * -------------------
 *
 * Model fields are declared with on object keys of which act as the field name and
 * values that define the field attributes.
 *
 *
 * field attributes:
 * -----------------
 *
 * - type: Data type of the field (mandatory).
 * - default: Default value for the field (mandatory).
 * - enum: Array of possible values the field can have if it's type declaration contains
 *         the enum type.
 * - model: Class/constructor reference to complex type. Needed if the type declaration
 *          contains and model reference. Only one model reference can be defined per field.
 * - optional: Is the field a mandatory or can it also contain an empty (null) value.
 *             Defaults to false.
 * - serialization: Can this field be converted to and from JSON. Can and must be used
 *                   in case this field contains an value that does not implement toJSON
 *                   or fromJSON functions. Defaults to ALWAYS.
 *                   Possible values are: FROM, TO, ALWAYS and NEVER
 *                   Use the FieldSerialization enum.
 *
 * Type syntax:
 * ------------
 * Types are declared with small recursive DSL:
 *    [primitive|model|enum|indexedCollection<type>|keydCollection<type,type>]
 *
 * Supported primitive types are:
 *   - any
 *   - string
 *   - number
 *   - bool
 *   - function
 *   - moment (not a real js primitive, but can be used without model declaration)
 *
 * Supported collection types:
 *   - List<valueType>
 *   - Set<valueType>
 *   - OrderedSet<valueType>
 *   - Map<valueType,keyType>
 *   - OrderedMap<valueType,keyType>
 *
 * Model types.
 *   To use complex types, such as other models, as field values in the model, one
 *   must set the type or partial to `model` and declare the model class reference
 *   with the additional model attribute.
 *
 * Enum types.
 *   To use enums ad type one must set the type or partial to `enum` and define the
 *   possible values with the additional enum attribute. Only one type of enum can
 *   be referenced per field at this moment.
 *
 *
 * Field declaration examples:
 * ---------------------------
 *
 * {
 *   name: { type: 'string', default: 'placeholder' },
 *   age: { type: 'number', default: 18 },
 *   isEvil: { type: 'bool', default: false },
 *   timeOfDeath: { type: 'moment', default: moment.utc() },
 *   species: { type: 'enum', enum: SpeciesEnumClass, default: SpeciesEnumClass.HUMAN },
 *
 *   error: {
 *     type: 'model',
 *     model: Error,
 *     default: null,
 *     optional: true,
 *     serialization: FieldSerialization.NEVER
 *   },
 *
 *   pets: { type: 'List<model>', model: PetModel, default: List() },
 *
 *   complexCollection: {
 *     type: 'List<Map<List<Set<number>>,string>>,
 *     default: List()
 *   }
 * }
 *
 * Here the error field contains an JS Error that cannot be converted or loaded from JSON,
 * thus it requires the serialization: FieldSerialization.NEVER attribute. It has also been marked
 * as optional, so the field noes not need to be populated with proper value.
 *
 *
 * React PropTypes:
 * ----------------
 *
 * React propType declaration can be accessed as MyModel.propType or MyModel.propType.isRequired.
 *
 * @param {object<string,{
 *   type: string,
 *   default: *,
 *   enum: (Enum|object<string,string>)=,
 *   model: Function=,
 *   optional: boolean=,
 *   serialization: FieldSerialization=
 * }>} fieldsRaw
 *
 * @returns {Function}
 */
function genModel(fieldsRaw) {
  const fields = ImmutableMap(fieldsRaw).map(field => ({
    ...field,
    isClassEnum: Object.prototype.isPrototypeOf.call(Enum, field.enum),
    serialization: field.serialization || FieldSerialization.ALWAYS
  }));

  const parseableFields = fields.filter(it =>
    [FieldSerialization.ALWAYS, FieldSerialization.FROM].includes(it.serialization));

  const unserializableFieldNames = fields
    .filter(it => [FieldSerialization.NEVER, FieldSerialization.FROM].includes(it.serialization))
    .keySeq()
    .toList();

  const defaultValues = ImmutableMap(fields).map(it => it.default).toObject();

  class Model extends Record(defaultValues) {
    static fields = fields.toObject();

    static propType = genPropTypeDeclaration(fields);

    /* eslint-disable no-underscore-dangle */
    // Provide a "empty" model singleton. Also allow calling it from multiple inheritance levels.
    static __emptyModels = new Map();

    static get empty() {
      if (this.__emptyModels.has(this)) {
        return this.__emptyModels.get(this);
      }

      const empty = new this();
      this.__emptyModels.set(this, empty);

      return empty;
    }
    /* eslint-enable no-underscore-dangle */

    static parseJSON(json) {
      return ImmutableMap().withMutations(mutable => {
        parseableFields.forEach((field, name) => {
          if (typeof json[name] !== 'undefined') {
            mutable.set(name, parseJSONToType(field, name, json[name]));
          }
        });
      });
    }

    static fromJSON(json) { return new this(this.parseJSON(json)) }

    toJSON() {
      if (unserializableFieldNames.size > 0) {
        return this.toMap()
          .withMutations(mut => { unserializableFieldNames.forEach(it => mut.delete(it)) })
          .toJSON();
      }

      return super.toJSON();
    }

    /* eslint-disable no-underscore-dangle */
    get isEmpty() {
      this.__isEmpty = this.__isEmpty ?? immutableCompare(this, this.constructor.empty);
      return this.__isEmpty;
    }
    /* eslint-enable no-underscore-dangle */

    mergeFromJSON(json) {
      return this.merge(this.constructor.parseJSON(json));
    }
  }

  return Model;
}

export default genModel;
